import type * as React from 'react';
import { useSyncExternalStore } from 'react';
import type { FlashbarProps } from '@cloudscape-design/components/flashbar';

/**
 * Sistema de notificações do admin baseado no `Flashbar` do Cloudscape
 * (substitui o `toast` do Sonner/ui-shared na migração que tira o Tailwind do
 * admin). Mantém uma API imperativa `toast.success/error/message` idêntica à do
 * Sonner para minimizar a troca nos call-sites — só muda a fonte do import.
 *
 * As mensagens vivem num store de módulo; o `<Flashbar>` no `AppLayout`
 * (slot `notifications`) lê via `useNotificationItems()`.
 */
// Origem: sem issue — commit f2543c415, 2026-06-09 (Flashbar do Cloudscape no
// lugar do Sonner). Usado por: `toast` em ~65 telas/diálogos do admin;
// `useNotificationItems` no `routes/AdminLayout.tsx`.
type FlashType = FlashbarProps.MessageDefinition['type'];

let items: readonly FlashbarProps.MessageDefinition[] = [];
const listeners = new Set<() => void>();
let counter = 0;
/** Timer de auto-dismiss por id: substituir o toast tem de cancelar o do anterior (#1021). */
const timers = new Map<string, ReturnType<typeof setTimeout>>();

/** Avisa os assinantes (`useSyncExternalStore`) que `items` mudou. */
function emit(): void {
  for (const listener of listeners) listener();
}

/** Remove a notificação `id` (no-op se já saiu). */
function dismiss(id: string): void {
  const next = items.filter((item) => item.id !== id);
  if (next.length !== items.length) {
    items = next;
    emit();
  }
}

// Auto-dismiss espelhando o comportamento do Sonner (some sozinho). Erro fica
// um pouco mais para dar tempo de ler. `duration: Infinity` persiste.
const AUTO_DISMISS_MS: Record<NonNullable<FlashType>, number> = {
  success: 5000,
  info: 6000,
  warning: 7000,
  error: 8000,
  'in-progress': 8000,
};

/** Opções do `toast` (subconjunto compatível com o Sonner). */
export type ToastOptions = {
  /** Reaproveita/atualiza uma notificação existente com o mesmo id. */
  id?: string;
  /** Título acima do conteúdo (Flashbar `header`). */
  header?: React.ReactNode;
  /** Texto secundário (compat com o `toast(title, { description })` do Sonner):
   *  vira o `content`, com o `title` indo para o `header`. */
  description?: React.ReactNode;
  /** Ação no canto da notificação (Flashbar `action`). */
  action?: React.ReactNode;
  /** Duração em ms até sumir; `Infinity` = não some sozinho. */
  duration?: number;
  /**
   * Chamado só quando a PESSOA fecha a notificação pelo X do Flashbar — não no
   * auto-dismiss nem no `toast.dismiss(id)` do código. Origem: #1021 (2026-10-25
   * 15:00 +0100): com `stickyNotifications`, o aviso de instalar (`duration:
   * Infinity`) fechado pelo X voltava a cada carga, fixo no topo, porque nada
   * gravava a recusa. Usado por: `components/InstallPrompt.tsx`.
   */
  onDismiss?: () => void;
};

/** Insere/substitui a notificação no topo e agenda o auto-dismiss. */
function push(
  type: NonNullable<FlashType>,
  content: React.ReactNode,
  opts?: ToastOptions,
): string {
  const id = opts?.id ?? `flash-${String((counter += 1))}`;
  // 2ª auditoria (2026-10-25 21:32 +0100, #1021): o timer do toast substituído (mesmo `id`) seguia
  // vivo e fechava o toast NOVO antes do prazo dele (erro repetido com o mesmo id sumia antes de ser lido).
  const previous = timers.get(id);
  if (previous !== undefined) {
    clearTimeout(previous);
    timers.delete(id);
  }
  const rest = items.filter((item) => item.id !== id);
  const item: FlashbarProps.MessageDefinition = {
    id,
    type,
    content,
    dismissible: true,
    onDismiss: () => {
      dismiss(id);
      // #1021: só o X do usuário chega aqui (auto-dismiss e `toast.dismiss` chamam `dismiss` direto).
      opts?.onDismiss?.();
    },
    ...(opts?.header !== undefined ? { header: opts.header } : {}),
    ...(opts?.action !== undefined ? { action: opts.action } : {}),
  };
  items = [item, ...rest];
  emit();

  const duration = opts?.duration ?? AUTO_DISMISS_MS[type];
  if (Number.isFinite(duration)) {
    timers.set(
      id,
      setTimeout(() => {
        timers.delete(id);
        dismiss(id);
      }, duration),
    );
  }
  return id;
}

/**
 * Chamada base `toast(message, opts)` (compat Sonner). Se `opts.description`
 * vier, `message` vira o título (header) e `description` o conteúdo.
 */
function baseToast(message: React.ReactNode, opts?: ToastOptions): string {
  if (opts?.description !== undefined) {
    return push('info', opts.description, { ...opts, header: message });
  }
  return push('info', message, opts);
}

/**
 * API compatível com o `toast` do Sonner: callable (`toast(msg)`) + métodos
 * `success/error/message/info/warning/dismiss`. `message` mapeia para o tipo
 * neutro `info` do Flashbar.
 */
export const toast = Object.assign(baseToast, {
  success: (content: React.ReactNode, opts?: ToastOptions): string =>
    push('success', content, opts),
  error: (content: React.ReactNode, opts?: ToastOptions): string =>
    push('error', content, opts),
  message: (content: React.ReactNode, opts?: ToastOptions): string =>
    push('info', content, opts),
  info: (content: React.ReactNode, opts?: ToastOptions): string => push('info', content, opts),
  warning: (content: React.ReactNode, opts?: ToastOptions): string =>
    push('warning', content, opts),
  dismiss,
});

/** Assinatura do store para o `useSyncExternalStore`. */
function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/** Snapshot imutável (nova referência a cada mudança). */
function getSnapshot(): readonly FlashbarProps.MessageDefinition[] {
  return items;
}

/** Hook para o `<Flashbar>` do `AppLayout` ler as notificações ativas. */
export function useNotificationItems(): readonly FlashbarProps.MessageDefinition[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
